import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Divider,
  Grid,
  Typography
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import DashboardLayout from '../../layout/dashboard/DashboardLayout';
import {
  formatFieldName,
  formatHistoryValue,
  getRelativeTime
} from '../../lib/historyUtils';
import { fetchProjectHistory } from '../../modules/project';
import PaginationComponent from '../elements/PaginationComponent';

const ITEMS_PER_PAGE = 10;

const getInitials = (user) => {
  if (!user) return '?';
  return `${user.first_name?.[0] || ''}${user.last_name?.[0] || ''}`.toUpperCase();
};

const isLongText = (value) =>
  typeof value === 'string' && value.length > 80;

const ProjectHistory = () => {
  const { projectId } = useParams();
  const [page, setPage] = useState(1);
  const [expanded, setExpanded] = useState({});

  const { data = [], isLoading } = useQuery({
    queryKey: ['project-history', projectId],
    queryFn: () => fetchProjectHistory(projectId),
    enabled: !!projectId
  });

  const totalPages = Math.ceil(data.length / ITEMS_PER_PAGE);

  const paginatedData = data.slice(
    (page - 1) * ITEMS_PER_PAGE,
    page * ITEMS_PER_PAGE
  );

  const toggleExpand = (id) => {
    setExpanded((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const renderUpdateMessages = (entry) => {
    if (!entry?.changes) return null;

    return Object.entries(entry.changes).map(([field, value]) => {
      if (field === 'linked_jira_projects_added' && Array.isArray(value)) {
        return (
          <Box key={field} sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
              Linked Jira projects:
            </Typography>

            {value.map((name, index) => (
              <Typography key={index} variant="body2">
                • {name}
              </Typography>
            ))}
          </Box>
        );
      }

      if (field === 'linked_jira_projects_removed' && Array.isArray(value)) {
        return (
          <Box key={field} sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
              Unlinked Jira projects:
            </Typography>

            {value.map((name, index) => (
              <Typography key={index} variant="body2">
                • {name}
              </Typography>
            ))}
          </Box>
        );
      }

      const oldVal = value?.old_value;
      const newVal = value?.new_value;

      if (field === 'mandatory_assigned_to') {
        return (
          <Typography key={field} variant="body2" sx={{ mb: 1 }}>
            Mandatory assignment of test cases within a test cycle was{' '}
            {newVal ? 'enabled' : 'disabled'}
          </Typography>
        );
      }

      if (field === 'review_workflow') {
        return (
          <Typography key={field} variant="body2" sx={{ mb: 1 }}>
            Review workflow was {newVal ? 'enabled' : 'disabled'}
          </Typography>
        );
      }

      if (isLongText(oldVal) || isLongText(newVal)) {
        return (
          <Box key={field} sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
              {formatFieldName(field)}
            </Typography>

            <Typography variant="caption" color="text.secondary">
              Previous
            </Typography>
            <Typography
              variant="body2"
              sx={{ mb: 1, whiteSpace: 'pre-line' }}
            >
              {formatHistoryValue(oldVal)}
            </Typography>

            <Typography variant="caption" color="text.secondary">
              New
            </Typography>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
              {formatHistoryValue(newVal)}
            </Typography>
          </Box>
        );
      }

      if (oldVal == null) {
        return (
          <Typography key={field} variant="body2" sx={{ mb: 1 }}>
            <strong>{formatFieldName(field)}</strong> set to{' '}
            {formatHistoryValue(newVal)}
          </Typography>
        );
      }

      return (
        <Typography key={field} variant="body2" sx={{ mb: 1 }}>
          <strong>{formatFieldName(field)}</strong> changed from{' '}
          {formatHistoryValue(oldVal)} to{' '}
          {formatHistoryValue(newVal)}
        </Typography>
      );
    });
  };

  return (
    <DashboardLayout>
      <Grid
        container
        maxWidth="md"
        sx={{
          minHeight: 'calc(100vh - 120px)',
          flexDirection: 'column',
        }}
      >
        <Grid item xs={12} mb={3}>
          <Typography variant="h4">History</Typography>
        </Grid>

        {isLoading && (
          <Box textAlign="center">
            <CircularProgress size={24} />
          </Box>
        )}

        {!isLoading && paginatedData.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No history available.
          </Typography>
        )}

        <Box sx={{ flex: 1 }}>
          {!isLoading &&
            paginatedData.map((entry, index) => {
              const showAll = expanded[entry.id];

              const allMessages =
                entry.action_type === 'UPDATE'
                  ? renderUpdateMessages(entry)
                  : [];

              const visibleMessages = showAll
                ? allMessages
                : allMessages?.slice(0, 3);

              return (
                <Box key={entry.id} py={2}>
                  {/* Header */}
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={1}
                  >
                    <Box display="flex" alignItems="center" gap={1.5}>
                      <Avatar sx={{ width: 26, height: 26, fontSize: 12 }}>
                        {getInitials(entry.performed_by_user)}
                      </Avatar>

                      <Typography variant="body2" fontWeight={500}>
                        {entry.performed_by_user?.first_name}{' '}
                        {entry.performed_by_user?.last_name}{' '}
                        {entry.action_type === 'UPDATE'
                          ? 'updated'
                          : entry.action_type === 'CREATE'
                            ? 'created'
                            : 'deleted'}{' '}
                        this project
                      </Typography>
                    </Box>

                    <Typography
                      variant="caption"
                      color="text.secondary"
                    >
                      {getRelativeTime(entry.created_at)}
                    </Typography>
                  </Box>

                  {/* Changes */}
                  <Box pl={5}>
                    {visibleMessages}

                    {allMessages?.length > 3 && (
                      <Button
                        size="small"
                        onClick={() => toggleExpand(entry.id)}
                        sx={{ mt: 0.5 }}
                      >
                        {showAll ? 'Show less' : 'Show more'}
                      </Button>
                    )}
                  </Box>

                  {index !== paginatedData.length - 1 && (
                    <Divider sx={{ mt: 2 }} />
                  )}
                </Box>
              );
            })}
        </Box>

        {!isLoading && totalPages > 1 && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              mt: 3,
              pb: 2,
            }}
          >
            <PaginationComponent
              totalCount={data.length}
              currentPage={page}
              handlePageChange={setPage}
              perPage={ITEMS_PER_PAGE}
            />
          </Box>
        )}
      </Grid>
    </DashboardLayout>
  );
};

export default ProjectHistory;